import React, { Component } from 'react';
import { connect } from 'react-redux';
import { findIndex } from 'lodash';
import Dotdotdot from 'react-dotdotdot';
import PropTypes from 'prop-types';
import { getEventDateStringV2 } from '../../../utils/formatting';
import Description from '../../text/Description';
import { LinkAnchor } from '../../Links';
import AddEventToCalendar from '../../AddToCalander';
import { getEventRedirectUrl, getUseAnchorForEventLink, isPrivateEvent, getPaidHitterExperience, EventTypes, isPickleBallSportTypeEvent, isEventType } from '../../../utils/events';
import { Utr } from '../../text';
import SportLabel from '../CardComponents/SportLabel';
import { ratingTypes } from '../../../utils/constants';
import USAPLogo from '../../USAPLogo';
import { TmsEventUsapApplicationStatusTypes, sportTypeIds } from '../../../utils/eventConstants';
import Img from '../../Img';

const _ = { findIndex };

/*
    Props:
    tourTag - Manually override the displayed tour on the result
*/

class EventCard extends Component {
    constructor(props) {
        super(props);
        this.trimStringToWords = this.trimStringToWords.bind(this);
        this.formatEventName = this.formatEventName.bind(this);
    }

    trimStringToWords(str, numWords) {
        const words = str.split('');
        if (words.length > numWords) {
            return `${words.slice(0, numWords).join('')}...`;
        }
        return str;
    }

    formatEventName(str) {
        if (this.props.isOnDashboard || this.props.isProfilePage) {
            const string = str.replace(/_/g, ' ');
            return this.trimStringToWords(string, 40);
        }

        return str.replace(/_/g, ' ');
    }

    render() {
        const { force = {}, forceCardView, auth, tourTag, event, isAdminOrOwner, selectButton, onSelectButtonClick, showInvitation, onAcceptClick, onDeclineClick, invitingMember, showRegister, coreUI, isOnDashboard = false, showCompleteRegistration = false, 'data-testid': dataTestId } = this.props;
        const {
            id,
            name,
            isDemoEvent,
            published,
            eventDirectors,
            eventState,
            eventLocations,
            utrType,
            currentMember,
            club,
            source,
            eventType,
            eventTours,
            teamType,
            utrRange,
            priceRange,
            priceRangeShort,
            gender,
            hasPrizeMoney,
            prizeMoneyText,
            eventSchedule,
            eventSettings,
            paidHits,
            sportTypeId,
            registeredCount,
            isRecurringEvent,
        } = this.props.event;
        const playerExperienceTypesObject = coreUI?.types.playerExperienceTypes;
        const coachExperienceTypesObject = coreUI?.types.coachingExperienceTypes;
        const isFreePlayEvent = isEventType(event, EventTypes.FREE_PLAY);
        const isPaidHitEvent = isEventType(event, EventTypes.PAID_HIT);
        const shouldHideClubName = isFreePlayEvent || isPaidHitEvent;
        const isPickleBallEvent = isPickleBallSportTypeEvent(sportTypeId);
        const gameTypes = coreUI?.types?.tms?.gameTypes;
        const usapVerificationTypeId = event?.usapVerificationTypeId;
        const usapApplicationStatusId = event?.usapApplicationStatusId;
        const locationCityStateZip = (eventLocations && eventLocations.length > 0) ? eventLocations[0].display || eventLocations[0].placeName : null;
        const cardHeight = 205;
        const verifiedClassName = sportTypeId === sportTypeIds.PICKLEBALL ? 'icon-verified-green' : 'icon-verified-blue';

        const getGameType = () => {
            return eventType.id !== EventTypes.FREE_PLAY
                ? teamType
                : gameTypes.find(x => x.id === eventSettings?.gameTypeId)?.label;
        };

        const userIsPowered = auth?.user?.isPowered;
        const isPlaying = currentMember && (currentMember.playing || currentMember.isPlaying);
        const isOrganizer = (auth?.user?.id && eventDirectors &&
            (_.findIndex(eventDirectors, { memberId: auth.user.id }) !== -1)) ||
            currentMember?.eventDirectorRole || isAdminOrOwner;

        /* Description items desktop */
        const descriptionLine1 = [locationCityStateZip];
        if (hasPrizeMoney && prizeMoneyText && !isPaidHitEvent) {
            descriptionLine1.push(prizeMoneyText);
        }

        if ((isOrganizer || event?.playerCountVisibility) && registeredCount > 0) {
            descriptionLine1.push(`${registeredCount} Players`);
        }

        const descriptionLine2 = [];
        if (priceRange) {
            descriptionLine2.push(priceRange);
        }
        if (utrRange) {
            if (isPickleBallEvent) {
                descriptionLine2.push(`${ratingTypes.pickleball} ${utrRange}`);
            } else {
                descriptionLine2.push(`${ratingTypes.tennis} ${utrRange}`);
            }
        }
        if (gender && !isPaidHitEvent) {
            descriptionLine2.push(gender);
        }
        if ((teamType || eventSettings?.gameTypeId) && !isPaidHitEvent) {
            const gameType = getGameType();
            descriptionLine2.push(gameType);
        }

        const isImportedEvent = source === 'scraped-import';

        const isClubMember = (auth?.user?.id && club &&
            (_.findIndex(auth.user.clubMemberships, { clubId: club.id }) !== -1));

        const showMemberEventTag = isPrivateEvent(this.props.event) && !isClubMember;

        const colorStateV2 = () => {
            switch (eventState?.value) {
                case 'pre_registration':
                case 'completed':
                    return 'gray';
                case 'in_progress':
                    return '';
                default:
                    return '';
            }
        };

        const getTag = (block, tagPrefix) => {
            if (!published) {
                return <div className={`tag tag-gray tag-xs ${block ? 'tag-block' : ''}`} data-testid={tagPrefix && `${tagPrefix}.status-tag`}>Draft</div>;
            }
            if (isPlaying && isOrganizer) {
                return (
                    <>
                        <div className={`tag tag-${colorStateV2()} tag-xs mr8`} data-testid={tagPrefix && `${tagPrefix}.player-tag`}>Player</div>
                        <div className={`tag tag-${colorStateV2()} tag-xs`} data-testid={tagPrefix && `${tagPrefix}.provider-tag`}>Provider</div>
                    </>
                );
            }
            if (isPlaying) {
                return <div className={`tag tag-${colorStateV2()} tag-xs ${block ? 'tag-block' : ''}`} data-testid={tagPrefix && `${tagPrefix}.player-tag`}>Player</div>;
            }
            if (isOrganizer) {
                return <div className={`tag tag-${colorStateV2()} tag-xs ${block ? 'tag-block' : ''}`} data-testid={tagPrefix && `${tagPrefix}.provider-tag`}>Provider</div>;
            }
            if (showMemberEventTag) {
                return <div className={`tag tag-gray tag-xs ${block ? 'tag-block' : ''}`} data-testid={tagPrefix && `${tagPrefix}.status-tag`}>Member Event</div>;
            }

            switch (eventState?.value) {
                case 'pre_registration':
                    return (
                        <div className={`tag tag-gray tag-xs ${block ? 'tag-block' : ''}`} data-testid={tagPrefix && `${tagPrefix}.status-tag`}>
                            <span className={`d-block d-lg-none`} data-testid={tagPrefix && `${tagPrefix}.status-tag.short`}>Pre Reg</span>
                            <span className={`d-none d-lg-block`} data-testid={tagPrefix && `${tagPrefix}.status-tag.long`}>Pre Registration</span>
                        </div>
                    );
                case 'open_registration':
                    return (
                        <div className={`tag tag-outline tag-xs ${block ? 'tag-block' : ''}`} data-testid={tagPrefix && `${tagPrefix}.status-tag`}>
                            <span className={`d-block d-lg-none`} data-testid={tagPrefix && `${tagPrefix}.status-tag.short`}>Reg Open</span>
                            <span className={`d-none d-lg-block`} data-testid={tagPrefix && `${tagPrefix}.status-tag.long`}>Registration Open</span>
                        </div>
                    );
                case 'registration_closed':
                    return (
                        <div className={`tag tag-gray tag-xs ${block ? 'tag-block' : ''}`} data-testid={tagPrefix && `${tagPrefix}.status-tag`}>
                            <span className={`d-block d-lg-none`} data-testid={tagPrefix && `${tagPrefix}.status-tag.short`}>{isImportedEvent ? 'Scheduled' : 'Reg Closed'}</span>
                            <span className={`d-none d-lg-block`} data-testid={tagPrefix && `${tagPrefix}.status-tag.long`}>{isImportedEvent ? 'Scheduled' : 'Registration Closed'}</span>
                        </div>
                    );
                case 'in_progress':
                    return <div className={`tag tag-xs ${block ? 'tag-block' : ''}`} data-testid={tagPrefix && `${tagPrefix}.status-tag`}>In Progress</div>;
                case 'completed':
                    return <div className={`tag tag-gray tag-xs ${block ? 'tag-block' : ''}`} data-testid={tagPrefix && `${tagPrefix}.status-tag`}>Completed</div>;
                default:
                    return <div />;
            }
        };

        const descriptionRight = [];
        if (eventType?.label) {
            descriptionRight.push(eventType.label);
        }
        const tourName = tourTag || (eventTours?.length && eventTours[0].name);
        if (tourName) {
            descriptionRight.push(tourName);
        }

        const clubDetails = shouldHideClubName ? [] : [club?.name];
        clubDetails.push(locationCityStateZip);

        const descriptionMobileLine1V2 = [];
        const descriptionMobileLine2V2 = [];

        /* Description items mobile */
        if (hasPrizeMoney && prizeMoneyText && !isPaidHitEvent) {
            descriptionMobileLine1V2.push(prizeMoneyText);
        }

        if ((isOrganizer || event?.playerCountVisibility) && registeredCount > 0) {
            descriptionMobileLine2V2.push(`${registeredCount} Players`);
        }
        if (utrRange) {
            if (isPickleBallEvent) {
                descriptionMobileLine2V2.push(`${ratingTypes.pickleball} ${utrRange}`);
            } else {
                descriptionMobileLine2V2.push(`${ratingTypes.tennis} ${utrRange}`);
            }
        }
        if (priceRangeShort) {
            descriptionMobileLine1V2.push(`${priceRangeShort}`);
        }
        if (gender && !isPaidHitEvent) {
            descriptionMobileLine2V2.push(gender);
        }
        if (teamType && !isPaidHitEvent) {
            const gameType = getGameType();
            descriptionMobileLine2V2.push(gameType);
        }

        let eventDateString;
        if (eventSchedule?.eventDatesString) {
            eventDateString = eventSchedule.eventDatesString;
        } else if (eventSchedule) {
            eventDateString = getEventDateStringV2(eventSchedule);
        }

        return (
            <div style={showMemberEventTag ? { pointerEvents: 'none' } : {}}>
                <LinkAnchor
                    link={getEventRedirectUrl(id, source)}
                    useAnchor={getUseAnchorForEventLink(source)}
                    target={'_self'}
                    data-testid={dataTestId && `${dataTestId}.card-link`}
                >
                    <div
                        // eslint-disable-next-line max-len
                        className={`utr-card event-card  ${force?.large ? 'event-card-large' : ''} ${force?.featured ? 'event-card-featured' : ''}`}
                        style={{ minHeight: (usapApplicationStatusId || usapVerificationTypeId) ? cardHeight : '', height: (usapApplicationStatusId || usapVerificationTypeId) ? 'fit-content' : '' }}
                    >
                        <SportLabel sportTypeId={sportTypeId} data-testid={dataTestId && `${dataTestId}.sport-label`} />
                        <div className={` list-view  ${forceCardView || force.cardView ? 'd-none' : 'd-none d-md-block'} bb0`}>
                            <div className="row">
                                <div className="col-md-6 content-wrapper">
                                    <div className="club-and-name">
                                        <div className="club-name show-ellipsis" data-testid={dataTestId && `${dataTestId}.desktop.club-name`}>{!isPaidHitEvent && club?.name}</div>
                                        <div className={`name ${utrType?.value === 'sanctioned' ? 'hasSanctioned' : ''}`} title={name} data-testid={dataTestId && `${dataTestId}.desktop.name`}>
                                            <Dotdotdot clamp={1}>
                                                {isDemoEvent ? '[DEMO] ' : ''}{this.formatEventName(name)}
                                            </Dotdotdot>
                                        </div>
                                    </div>

                                    <div className="description">
                                        {isPaidHitEvent && (
                                            <div className={'text-black'} data-testid={dataTestId && `${dataTestId}.desktop.paid-hitter-experience`}>
                                                {getPaidHitterExperience(paidHits, playerExperienceTypesObject, coachExperienceTypesObject, userIsPowered, isOrganizer)}
                                            </div>
                                        )}
                                        <div data-testid={dataTestId && `${dataTestId}.desktop.description-line1`}>
                                            <Description desc={descriptionLine1} data-testid={dataTestId && `${dataTestId}.desktop.description-line1`} />
                                        </div>
                                        <div data-testid={dataTestId && `${dataTestId}.desktop.description-line2`}>
                                            <Description desc={descriptionLine2} data-testid={dataTestId && `${dataTestId}.desktop.description-line2`} />
                                        </div>
                                    </div>
                                    <div>
                                        {isPickleBallEvent && <USAPLogo usapApplicationStatusId={usapApplicationStatusId} usapVerificationTypeId={usapVerificationTypeId} data-testid={dataTestId && `${dataTestId}.desktop.usap-logo`} />}
                                    </div>

                                </div>
                                <div className="col-md-6 content-wrapper aie">
                                    <div className="date-wrapper">
                                        <div className={`d-flex jce aic mb8`}>
                                            <div className={`tags`}>
                                                {getTag(false, dataTestId && `${dataTestId}.desktop`)}
                                            </div>
                                            {((utrType?.value === 'sanctioned') || hasPrizeMoney) && (
                                                <div className={`badge-wrapper text-teal ml4`} data-testid={dataTestId && `${dataTestId}.desktop.badge-wrapper`}>
                                                    {(utrType?.value === 'sanctioned') && (
                                                        <div className={`utr-icon ${verifiedClassName} ml12`} title="Sanctioned" data-testid={dataTestId && `${dataTestId}.desktop.sanctioned-badge`} />
                                                    )}
                                                    {hasPrizeMoney &&
                                                        <i className="prize-event-icon fas fa-usd-circle ml12" title="Has Prize Money" data-testid={dataTestId && `${dataTestId}.desktop.prize-money-badge`} />}
                                                </div>
                                            )}
                                        </div>
                                        <div className="date" data-testid={dataTestId && `${dataTestId}.desktop.date`}>
                                            {eventDateString}
                                        </div>
                                    </div>
                                    {selectButton ? (
                                        <button
                                            type="button"
                                            className={`btn btn-primary btn-sm`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                onSelectButtonClick(id);
                                            }}
                                            data-testid={dataTestId && `${dataTestId}.desktop.select-btn`}
                                        >
                                            Select
                                        </button>
                                    ) : (
                                        <div className="description-right show-ellipsis" data-testid={dataTestId && `${dataTestId}.desktop.description-right`}><Description desc={descriptionRight} data-testid={dataTestId && `${dataTestId}.desktop.description-right`} /></div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div
                            // eslint-disable-next-line max-len
                            className={`card-view ${forceCardView || force.cardView ? 'd-block' : 'd-block d-md-none'} ${force?.small ? 'card-small' : ''} ${(showInvitation || showRegister || showCompleteRegistration) && 'invitation'}`}
                            style={{ minHeight: ((usapApplicationStatusId || usapVerificationTypeId) && usapApplicationStatusId !== TmsEventUsapApplicationStatusTypes.DECLINED) ? cardHeight : '', height: ((usapApplicationStatusId || usapVerificationTypeId) && usapApplicationStatusId !== TmsEventUsapApplicationStatusTypes.DECLINED) ? 'fit-content' : '' }}
                        >
                            <div className="content-wrapper">
                                <div className="club-and-name">
                                    <div className="date">
                                        <span data-testid={dataTestId && `${dataTestId}.mobile.date`}>{eventDateString}</span>
                                        <div className={`tags`}>
                                            {isRecurringEvent && isOrganizer && (
                                                <div className={`tag tag-xs`} data-testid={dataTestId && `${dataTestId}.mobile.recurring-tag`}>
                                                    <Img src="/img/v4/icon-recurring.svg" />
                                                    Recurring Event
                                                </div>
                                            )}
                                            {getTag(false, dataTestId && `${dataTestId}.mobile`)}
                                        </div>
                                    </div>
                                    <div className={`name d-flex break-word ${utrType?.value === 'sanctioned' ? 'hasSanctioned' : ''}`} title={name} data-testid={dataTestId && `${dataTestId}.mobile.name`}>
                                        <Dotdotdot clamp={isPaidHitEvent ? 1 : 2}>
                                            {this.formatEventName(name)}
                                        </Dotdotdot>
                                        <div className={`badge-wrapper`} data-testid={dataTestId && `${dataTestId}.mobile.badge-wrapper`}>
                                            {(utrType?.value === 'sanctioned') &&
                                                <div className={`utr-icon ${verifiedClassName}`} title="Sanctioned" data-testid={dataTestId && `${dataTestId}.mobile.sanctioned-badge`} />}
                                        </div>
                                    </div>
                                    {isPaidHitEvent && (
                                        <Dotdotdot clamp={1}>
                                            <div data-testid={dataTestId && `${dataTestId}.mobile.paid-hitter-experience`}>
                                                {getPaidHitterExperience(paidHits, playerExperienceTypesObject, coachExperienceTypesObject, userIsPowered, isOrganizer)}
                                            </div>
                                        </Dotdotdot>
                                    )}
                                    <div className="club-name show-ellipsis" data-testid={dataTestId && `${dataTestId}.mobile.club-name`}><Description desc={clubDetails} data-testid={dataTestId && `${dataTestId}.mobile.club-name`} /></div>
                                </div>
                                <div className="description">
                                    {!showInvitation && (
                                        <>
                                            <div data-testid={dataTestId && `${dataTestId}.mobile.description-line1`}>
                                                <Description desc={descriptionMobileLine1V2} data-testid={dataTestId && `${dataTestId}.mobile.description-line1`} />
                                            </div>
                                            <div data-testid={dataTestId && `${dataTestId}.mobile.description-line2`}>
                                                <Description desc={descriptionMobileLine2V2} data-testid={dataTestId && `${dataTestId}.mobile.description-line2`} />
                                                {selectButton && (
                                                    <div className="select">
                                                        <button
                                                            type="button"
                                                            className={`btn btn-primary btn-sm`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                e.preventDefault();
                                                                onSelectButtonClick(id);
                                                            }}
                                                            data-testid={dataTestId && `${dataTestId}.mobile.select-btn`}
                                                        >
                                                            Select
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                    {showInvitation && invitingMember && (
                                        <div className="text-grey d-flex aic mb24" data-testid={dataTestId && `${dataTestId}.mobile.invitation-text`}>
                                            <div className="utr-icon icon-profile mr8" />
                                            {invitingMember.firstName} {invitingMember.lastName[0]}. (<Utr player={invitingMember} force={{ ratingType: 'singles', showDecimals: true }} sportTypeId={sportTypeId} data-testid={dataTestId && `${dataTestId}.mobile.invitation-utr`} />) invited you to play
                                        </div>
                                    )}
                                    {showInvitation && isFreePlayEvent && (
                                        <div className="d-flex mb16">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-default text-uppercase ml-auto mr16 mb8"
                                                onClick={onDeclineClick}
                                                data-testid={dataTestId && `${dataTestId}.mobile.decline-btn`}
                                            >
                                                {'I\'ll Pass'}
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-primary text-uppercase mb8"
                                                onClick={onAcceptClick}
                                                data-testid={dataTestId && `${dataTestId}.mobile.accept-btn`}
                                            >
                                                {'I\'ll Play'}
                                            </button>
                                        </div>
                                    )}
                                    {showRegister && (
                                        <div className="col-12 text-center mb16">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-primary text-uppercase mb8"
                                                data-testid={dataTestId && `${dataTestId}.mobile.register-btn`}
                                            >
                                                Register
                                            </button>
                                        </div>
                                    )}
                                </div>
                                {showCompleteRegistration && (
                                    <div className="mt8">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-primary text-uppercase mb8"
                                            data-testid={dataTestId && `${dataTestId}.mobile.complete-registration-btn`}
                                        >
                                            Complete Registration
                                        </button>
                                    </div>
                                )}
                                {isPickleBallEvent && <USAPLogo usapApplicationStatusId={usapApplicationStatusId} usapVerificationTypeId={usapVerificationTypeId} data-testid={dataTestId && `${dataTestId}.mobile.usap-logo`} />}
                            </div>
                        </div>
                    </div>
                </LinkAnchor>
                {isOnDashboard && !showCompleteRegistration && <AddEventToCalendar isOnDashboard={isOnDashboard} uiElement={'link'} eventDetails={event} loggedInUserId={auth?.user?.id} data-testid={dataTestId && `${dataTestId}.add-to-calendar`} />}
            </div>
        );
    }
}

function mapStateToProps({ coreUI }) {
    return { coreUI };
}

EventCard.propTypes = {
    coreUI: PropTypes.shape().isRequired,
    event: PropTypes.shape().isRequired,
    showCompleteRegistration: PropTypes.bool,
    auth: PropTypes.shape({}).isRequired,
    isOnDashboard: PropTypes.bool,
    showRegister: PropTypes.bool,
    onDeclineClick: PropTypes.func,
    onAcceptClick: PropTypes.func,
    showInvitation: PropTypes.bool,
    onSelectButtonClick: PropTypes.func,
    isAdminOrOwner: PropTypes.bool,
    tourTag: PropTypes.shape({}),
    force: PropTypes.shape({}),
    forceCardView: PropTypes.shape({}),
    'data-testid': PropTypes.string,
};

EventCard.defaultProps = {
    showCompleteRegistration: false,
    isOnDashboard: false,
    showRegister: false,
    onDeclineClick: () => { },
    onAcceptClick: () => { },
    showInvitation: false,
    onSelectButtonClick: () => { },
    isAdminOrOwner: false,
    tourTag: null,
    force: {},
    forceCardView: null,
    'data-testid': null,
};

export default connect(mapStateToProps)(EventCard);
